/**
 * Photography used by the authored screens.
 *
 * Sourced from Unsplash (free licence, no attribution required) and re-hosted
 * through Agentplace Media, so the site never depends on a third-party CDN
 * staying put. Every URL below was verified to return an image before use.
 *
 * These are stock illustrations of a CATEGORY (sea, mountains, a city), never a
 * claim about a specific hotel, room, or destination we are selling.
 */

const MEDIA_BASE =
  'https://storage.agentplace.io/727_ScYyEw-MDQwVJ1dBN1b_eSO7MBQcf1Sa39FMKeE/c7fxfuoxn1zt/media';

export const HERO_IMAGE = `${MEDIA_BASE}/1789114543379-4949e246-557f-4fb8-ac28-82fd585419a3-hero-coastline.jpg`;

/**
 * Owner-supplied showreel used as the backdrop of step one. 24s, ~8.8MB, with
 * an audio track we always mute (autoplay requires it, and an unsolicited
 * soundtrack is hostile). Plays muted, looping and inline; visitors who prefer
 * reduced motion get it paused with controls instead.
 */
export const KICKOFF_VIDEO = `${MEDIA_BASE}/1789157909052-9b45e5d6-f679-4e53-bef0-20fba7804787-places-06.mp4`;

/**
 * Owner-supplied sprite atlas for the traveller on the quest board: a 359×1436
 * PNG holding four top-down frames of 359×359 stacked vertically. The BOTTOM
 * frame is the standing pose the walk starts and ends on; the cycle then runs
 * upward through the sheet (bottom → top) and loops.
 */
export const WALKER_ATLAS = `${MEDIA_BASE}/1789174297086-2ed01d01-9fda-45f6-a2ba-f294edc7b2a9-walker-atlas.png`;

/** Frames in the atlas, and the side of one square frame. */
export const WALKER_FRAMES = 4;

/** One photo per trip style — keys match the `/trip/style` values. */
export const STYLE_IMAGES: Record<string, string> = {
  sea: `${MEDIA_BASE}/1789187067157-43086c62-3fbd-4de5-9969-1905db263a4a-style-sea.jpg`,
  extreme: `${MEDIA_BASE}/1789114543374-46e414d9-52e3-4e2c-8bb9-b8a9308d5e53-style-extreme.jpg`,
  civilisation: `${MEDIA_BASE}/1789114543148-6bd29446-8602-4e71-8854-1ec0f9f00734-style-civilisation.jpg`,
  greenery: `${MEDIA_BASE}/1789187067164-bb5f529f-67f5-4b17-b060-f64a27023a44-style-greenery.jpg`,
  business: `${MEDIA_BASE}/1789187378522-9aa99e9b-8445-46cc-88f3-f551a8bb810b-style-business.jpg`,
};

/**
 * A photo for every other choice on the board, so no stop falls back to a bare
 * icon. Keys match the values written to the data model:
 * `/season`, `/visa`, `/days` and `/budget`.
 *
 * Same rule as above: each one illustrates a CATEGORY — a season, a document, a
 * length of trip, a price level — and never a specific hotel, room or place we
 * are offering.
 */
export const SEASON_IMAGES: Record<string, string> = {
  summer: `${MEDIA_BASE}/1789187067173-61b9244b-6737-4515-a146-bdec1a2a7403-weather-summer.jpg`,
  spring: `${MEDIA_BASE}/1789187067289-0bd42627-7c33-4bbd-888a-8efd14f33088-weather-spring.jpg`,
  autumn: `${MEDIA_BASE}/1789187067304-ffe94a59-4773-4f12-b25b-285359f91345-weather-autumn.jpg`,
  winter: `${MEDIA_BASE}/1789187067332-6e468d14-341f-4153-bbd3-c9907796ec0f-weather-winter.jpg`,
};

export const VISA_IMAGES: Record<string, string> = {
  yes: `${MEDIA_BASE}/1789187067354-3a39beee-b349-4ef8-82bd-9d9ddb5a8764-visa-yes.jpg`,
  no: `${MEDIA_BASE}/1789187067376-57f3c446-7ba8-4856-9671-25b31542a430-visa-no.jpg`,
  unsure: `${MEDIA_BASE}/1789187067439-a6ecc07b-3a47-479a-a0e6-39e8fecece48-visa-unsure.jpg`,
};

export const DURATION_IMAGES: Record<string, string> = {
  '4': `${MEDIA_BASE}/1789187067461-3a74ffa0-8efd-4513-8b99-3b4851e75ba3-days-4.jpg`,
  '7': `${MEDIA_BASE}/1789187067491-2aa50cb4-c77f-4ce5-b550-84ed74b049bb-days-7.jpg`,
  '10': `${MEDIA_BASE}/1789187067515-87497bd8-e773-4df0-9b29-53d1de2a634c-days-10.jpg`,
  '14': `${MEDIA_BASE}/1789187067535-8bb7f49b-c464-4415-9f6b-cd44e917d7fb-days-14.jpg`,
};

/** Owner-uploaded calendar image for the custom duration stop. */
export const CUSTOM_TIME_IMAGE =
  'https://storage.agentplace.io/727_ScYyEw-MDQwVJ1dBN1b_eSO7MBQcf1Sa39FMKeE/c7fxfuoxn1zt/media/1789192830627-64453ceb-49f9-460d-a63a-972e8ef9e0ea-custom-time-calendar.jpg';

export const BUDGET_IMAGES: Record<string, string> = {
  '800': `${MEDIA_BASE}/1789187067525-dcb61a57-c585-4318-9483-987c3294ad5f-budget-800.jpg`,
  '1400': `${MEDIA_BASE}/1789187067581-42f7939c-1a84-4c3b-9473-2374bcfd6e96-budget-1400.jpg`,
  '2500': `${MEDIA_BASE}/1789187067593-d4bc3eb1-2c9d-4692-bd9e-0f2d0563224a-budget-2500.jpg`,
  '4000': `${MEDIA_BASE}/1789187067631-ff42f043-7c33-4ce5-a972-59ba3a869faf-budget-4000.jpg`,
};

/**
 * Twenty curated holiday photographs used as the guaranteed placeholder for a
 * destination or hotel card whose real photo has not arrived (or failed).
 *
 * Every one was opened and inspected before it went in: pure place scenery —
 * beaches, lagoons, coastal cliffs, old towns, hotel pools and terraces. No
 * book covers, no animals, no people as the subject. Each was re-encoded at
 * 600×380 before being re-hosted here, so a card image is tens of kilobytes and
 * paints effectively instantly.
 *
 * These illustrate a CATEGORY of holiday scene. They are never a claim about
 * the specific place or hotel on the card, which is why the card labels them as
 * a placeholder rather than a photo of that property.
 */
export const VACATION_PLACEHOLDER_IMAGES: readonly string[] = [
  `${MEDIA_BASE}/1789257508137-bf58d810-5d2e-4056-a402-647c779a1ca1-vacation-01-pool-beach-moonrise.jpg`,
  `${MEDIA_BASE}/1789257508144-1ae62c42-c20c-4bdf-9189-34a33e1e31fe-vacation-02-resort-pool-palms.jpg`,
  `${MEDIA_BASE}/1789257508130-5f1171fb-27e9-43b5-ab6c-e2f93599a4cf-vacation-03-loungers-sea-pool.jpg`,
  `${MEDIA_BASE}/1789257508149-121cfb94-0234-4bc3-af6e-976875cea87f-vacation-04-cliffside-infinity-pool.jpg`,
  `${MEDIA_BASE}/1789257508248-ad7faeb3-f1f5-45a4-a759-9f0e3fb39369-vacation-05-pink-sand-aerial.jpg`,
  `${MEDIA_BASE}/1789257508251-1db19cc4-2b8d-4b0b-a002-9c0b0568e698-vacation-06-teal-surf-aerial.jpg`,
  `${MEDIA_BASE}/1789257508264-678e1d1b-8d20-4a76-a381-c2a0eb1f77f4-vacation-07-turquoise-wave-sand.jpg`,
  `${MEDIA_BASE}/1789257508268-eea86423-9ded-414c-95a2-6f57362515ad-vacation-08-palm-fringed-lagoon.jpg`,
  `${MEDIA_BASE}/1789257508293-13e775c4-0c13-498d-aaad-66f264a17eb2-vacation-09-white-sandbank.jpg`,
  `${MEDIA_BASE}/1789257508301-3bd0276f-4791-496e-8adf-8012506d5bbe-vacation-10-turquoise-reef-bay.jpg`,
  `${MEDIA_BASE}/1789257508311-c80f7bb1-7d0b-40d8-a478-40ec05625d2f-vacation-11-cliff-channel-lagoon.jpg`,
  `${MEDIA_BASE}/1789257508321-0f03600e-0ab9-403d-b51d-6d37036a935e-vacation-12-green-cliff-coastline.jpg`,
  `${MEDIA_BASE}/1789257508344-a773e450-7b50-4ef0-b814-9241abd2768f-vacation-13-coastal-bay-dusk.jpg`,
  `${MEDIA_BASE}/1789257508356-0d7eb6ca-35a0-46a0-887b-5e051aa2de28-vacation-14-white-waterfront-town.jpg`,
  `${MEDIA_BASE}/1789257508383-0f9f390e-73c6-4580-bafc-080dac616bd3-vacation-15-rooftops-bell-tower-sea.jpg`,
  `${MEDIA_BASE}/1789257508388-066e4287-b31c-4a26-a48c-b4a01e236855-vacation-16-riviera-cape-aerial.jpg`,
  `${MEDIA_BASE}/1789257508411-53a7e77f-c2b4-4fad-9221-fed66a059af6-vacation-17-terracotta-old-town-sea.jpg`,
  `${MEDIA_BASE}/1789257508427-d9e4b574-11d0-4cb6-b367-805859bf9922-vacation-18-stone-village-mountains.jpg`,
  `${MEDIA_BASE}/1789257508430-ea708d8d-bf84-492b-8e0a-216a24a0b891-vacation-19-cliffside-fishing-village.jpg`,
  `${MEDIA_BASE}/1789257508450-f58e0502-b439-4903-bdc6-ae45da7f8bf3-vacation-20-river-town-colourful.jpg`,
];

/**
 * The first four of the set are hospitality scenes (pools, terraces, loungers)
 * and back hotel cards; the remaining sixteen are destination scenery — beaches,
 * lagoons, cliffs, old towns — and back place cards. Splitting them this way
 * keeps a hotel card from falling back to an empty beach, and a place card from
 * falling back to somebody's swimming pool.
 */
export const HOTEL_PLACEHOLDER_IMAGES: readonly string[] = VACATION_PLACEHOLDER_IMAGES.slice(0, 4);

export const PLACE_PLACEHOLDER_IMAGES: readonly string[] = VACATION_PLACEHOLDER_IMAGES.slice(4);

/**
 * Owner-supplied traveller illustrations for the "how should we address you"
 * choice on the document: cut-out half-body figures on a transparent ground, in
 * our green. Keys match the `/gender` values.
 */
export const TRAVELLER_IMAGES: Record<string, string> = {
  female: `${MEDIA_BASE}/1789188580906-a7a3ba56-24a4-4b86-88a7-d986598c8f5b-traveller-female.png`,
  male: `${MEDIA_BASE}/1789188580901-5698875c-8131-4d41-b08e-929fa525cd94-traveller-male.png`,
};

/** Owner-supplied artwork for the Game for fun canyon run. */
export const GAME_IMAGES = {
  balloon: `${MEDIA_BASE}/1789296463636-66b6d6d3-92d8-4f59-9062-31225c2fdc27-baloon.png`,
  panel: `${MEDIA_BASE}/1789296463632-d515eb14-69b6-48ab-96c3-8839ff0f3d80-panel.png`,
  bomb: `${MEDIA_BASE}/1789302401771-11359524-decf-48f8-9986-046d609982f2-bomb.png`,
  questions: `${MEDIA_BASE}/1789296463640-d3d9e3c5-1985-4a53-bcb6-94edb6a312aa-questions.png`,
  third: `${MEDIA_BASE}/1789296463748-9c1f5098-1c9f-4855-810f-b80ce1b1bb3e-third.png`,
  noNumbers: `${MEDIA_BASE}/1789296463759-4b7e4454-e541-4c73-8380-9f43d7d3ada8-no-numbers.png`,
  withNumber: `${MEDIA_BASE}/1789296463795-cde1c2e3-d7fd-40e1-bf60-40c82982338f-with-number.png`,
  second: `${MEDIA_BASE}/1789307138155-73125aad-c7b9-4e43-b37c-8424e1ac6d11-second.png`,
  first: `${MEDIA_BASE}/1789296463831-5a7dbcb0-73a3-40f8-a8f4-360c635099dd-first.png`,
  bird: `${MEDIA_BASE}/1789326740311-aa74a938-b4d4-41f3-b66c-20685ee038b5-bird-replacement.png`,
} as const;
